/* @flow */

import classNames from 'classnames';
import { Formik, Form, Field } from 'formik';
import { compose } from 'ramda';
import * as React from 'react';
import { injectIntl, intlShape } from 'react-intl';
import { createUseStyles } from 'react-jss';
import * as Yup from 'yup';

import styles from 'src/shared/app/authentication/component/base/AuthenticationBase.style';
import AuthenticationFormHeader from 'src/shared/app/authentication/component/base/AuthenticationFormHeader';
import messages from 'src/shared/app/authentication/l10n/contactL10n';
import {
  usernameFieldMaxLength,
  firstNameFieldMaxLength,
  lastNameFieldMaxLength,
  supportMessageFieldMaxLength,
} from 'src/shared/app/authentication/settings/authenticationSettings';
import FormikFormGroup from 'src/shared/app/base/component/data-entry/form/FormikFormGroup';
import userMessages from 'src/shared/domain/user/l10n/userL10n';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';

type Props = {
  email: ?string,
  isSupportContacted: boolean,
  handleSubmit: (Object) => Promise<any>,
  // Self passed
  // $FlowIssue
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const SupportContactFormDumb = ({
  email,
  isSupportContacted,
  handleSubmit,
  intl,
}: Props): React.Node => {
  const schema = Yup.object().shape({
    first_name: Yup.string().required(),
    last_name: Yup.string().required(),
    email: Yup.string().required().email(),
    message: Yup.string().required(),
  });

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  const formClasses = classNames(classes.form, classes['form-isLong'], {
    [classes['form-isDisabled']]: isSupportContacted,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <Formik
      initialValues={{
        first_name: '',
        last_name: '',
        email: email || '',
        message: '',
      }}
      validationSchema={schema}
      validateOnBlur={true}
      onSubmit={(values, { setSubmitting, setStatus }) =>
        handleSubmit(values).catch(({ errors }) => {
          setSubmitting(false);

          const [error] = Object.values(errors || {});

          if (error) {
            setStatus(error);
          }
        })
      }
    >
      {({ status, dirty, isSubmitting }) => (
        <Form className={formClasses} noValidate>
          <AuthenticationFormHeader withError={!!status}>
            {status || intl.formatMessage(messages.legend)}
          </AuthenticationFormHeader>
          <Field
            id="first_name"
            name="first_name"
            type="text"
            component={FormikFormGroup}
            componentOpts={{
              label: intl.formatMessage(userMessages.firstName),
            }}
            maxLength={firstNameFieldMaxLength}
            required
            autoFocus
          />
          <Field
            id="last_name"
            name="last_name"
            type="text"
            component={FormikFormGroup}
            componentOpts={{
              label: intl.formatMessage(userMessages.lastName),
            }}
            maxLength={lastNameFieldMaxLength}
            required
          />
          <Field
            id="email"
            name="email"
            type="email"
            component={FormikFormGroup}
            componentOpts={{
              label: intl.formatMessage(messages.email),
            }}
            maxLength={usernameFieldMaxLength}
            required
          />
          <Field
            id="message"
            name="message"
            rows="3"
            component={FormikFormGroup}
            componentOpts={{
              useTextarea: true,
              label: intl.formatMessage(messages.message),
            }}
            maxLength={supportMessageFieldMaxLength}
            required
          />
          <div className={classes.submit}>
            <SecondaryButton isDisabled={!dirty} isSubmitting={isSubmitting}>
              {intl.formatMessage(messages.submit)}
            </SecondaryButton>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default compose(React.memo, injectIntl)(SupportContactFormDumb);
